import PageLayout from "@/components/layout/PageLayout";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowRight,
  Heart,
  Target,
  Zap,
  Shield,
  MapPin,
  Handshake,
  ShoppingBag,
  Rocket,
  Store,
  Package,
  Code,
  Building2,
} from "lucide-react";

const About = () => {
  const values = [
    {
      icon: Heart,
      title: "Kundennähe",
      description: "Persönlicher Service und individuelle Beratung stehen bei uns an erster Stelle.",
    },
    {
      icon: Target,
      title: "Qualität",
      description: "Wir vertreiben nur Software, von der wir selbst überzeugt sind.",
    },
    {
      icon: Zap,
      title: "Schnelligkeit",
      description: "Schnelle Lizenzbereitstellung und kurze Reaktionszeiten.",
    },
    {
      icon: Shield,
      title: "Vertrauen",
      description: "Transparente Preise, sichere Transaktionen, 100% deutsche Qualität.",
    },
  ];

  const timeline = [
    {
      year: "2024",
      title: "Gründung von Lizenzwerk",
      description: "Mit der Vision, Software-Vertrieb neu zu denken, wurde Lizenzwerk in Neugersdorf gegründet. Fokus auf Lizenzen und Software über eigene Kanäle.",
      icon: Rocket,
    },
    {
      year: "2024",
      title: "Multi-Channel-Expansion",
      description: "Aufbau des eigenen Online-Shops sowie erfolgreiche Integration in die Marktplätze eBay und Amazon.",
      icon: ShoppingBag,
    },
    {
      year: "2025",
      title: "Strategische Partnerschaft",
      description: "Beginn der Zusammenarbeit mit Launix – dem Entwickler professioneller Enterprise-Software aus Neugersdorf.",
      icon: Handshake,
    },
    {
      year: "2026",
      title: "Launch von NextInvoice",
      description: "Gemeinsam mit Launix bringen wir NextInvoice auf den Markt – die flexibelste Rechnungssoftware Deutschlands.",
      icon: Zap,
      highlight: true,
    },
  ];

  const channels = [
    {
      icon: Store,
      title: "Eigener Shop",
      description: "Unser Flaggschiff-Store mit persönlicher Beratung und schnellem Checkout.",
    },
    {
      icon: Package,
      title: "Amazon",
      description: "Bequemer Einkauf über den weltweit größten Online-Marktplatz.",
    },
    {
      icon: ShoppingBag,
      title: "eBay",
      description: "Langjähriger Top-Verkäufer mit exzellentem Kundenservice.",
    },
  ];

  return (
    <PageLayout>
      <Helmet>
        <title>Über uns – Lizenzwerk & Launix | NextInvoice</title>
        <meta 
          name="description" 
          content="Lizenzwerk & Launix: Zwei sächsische Unternehmen, eine Mission. Professionelle Software aus Neugersdorf – vom Entwickler bis zu Ihnen." 
        />
        <link rel="canonical" href="https://nxtinvoice.de/ueber-uns" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "Lizenzwerk24",
          "description": "Autorisierter Vertriebspartner für NextInvoice – modulare Geschäftssoftware für KMU.",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Hauptstraße 56",
            "addressLocality": "Neugersdorf",
            "postalCode": "02727",
            "addressRegion": "Sachsen",
            "addressCountry": "DE"
          },
          "telephone": "+4915565350880",
          "email": "info@nxtinvoice.de",
          "url": "https://nxtinvoice.de"
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Startseite", "item": "https://nxtinvoice.de/" },
            { "@type": "ListItem", "position": 2, "name": "Über uns" }
          ]
        })}</script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-background py-20 lg:py-28">
        <div className="absolute inset-0 gradient-hero pattern-grid" />
        <div className="absolute left-1/4 top-1/4 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute -right-20 bottom-0 h-72 w-72 rounded-full bg-accent/10 blur-3xl" />
        
        <div className="container relative">
          <div className="mx-auto max-w-4xl text-center">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-4 py-2">
              <Handshake className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold text-primary">Zwei Partner, eine Vision</span>
            </div>
            
            <h1 className="mb-6 font-display text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              Software aus Sachsen.
              <br />
              <span className="text-primary">Für ganz Deutschland.</span>
            </h1>
            
            <p className="mx-auto max-w-2xl text-lg leading-relaxed text-muted-foreground lg:text-xl">
              Lizenzwerk und Launix – zwei Unternehmen aus Neugersdorf, vereint durch die Leidenschaft 
              für großartige Software und exzellenten Service.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section - Product Promise */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary via-primary to-primary/90 py-20 lg:py-28">
        <div className="absolute inset-0 pattern-grid opacity-10" />
        <div className="absolute -left-32 -top-32 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-accent/20 blur-3xl" />
        
        <div className="container relative">
          <div className="mx-auto max-w-4xl text-center">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 backdrop-blur-sm">
              <Target className="h-4 w-4 text-accent" />
              <span className="text-sm font-semibold text-white">Unsere Mission</span>
            </div>
            
            <h2 className="mb-6 font-display text-3xl font-bold text-white sm:text-4xl lg:text-5xl">
              Software, die sich Ihnen anpasst –<br />
              <span className="text-accent">nicht umgekehrt</span>
            </h2>
            
            <p className="mx-auto mb-6 max-w-2xl text-lg text-white/90 leading-relaxed">
              Wir glauben, dass Rechnungssoftware sich an Unternehmen anpassen sollte. 
              Deshalb haben wir NextInvoice entwickelt: Eine Lösung, die so flexibel ist wie Ihr Business.
            </p>
            
            <p className="mx-auto max-w-2xl text-white/70">
              Von Selbstständigen bis zu Großunternehmen – wir verstehen, dass jedes Business einzigartig ist. 
              Mit NextInvoice können Sie nicht nur Rechnungen erstellen, sondern die gesamte Software an Ihre Arbeitsweise anpassen.
            </p>
          </div>
        </div>
      </section>

      {/* Partnership Story */}
      <section className="relative overflow-hidden bg-secondary/30 py-20 lg:py-28">
        <div className="absolute inset-0 pattern-dots opacity-30" />
        
        <div className="container relative">
          <div className="mb-16 text-center">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-4 py-2">
              <Handshake className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold text-primary">Die Partner hinter NextInvoice</span>
            </div>
            <h2 className="mb-4 font-display text-3xl font-bold text-foreground sm:text-4xl">
              Zwei Unternehmen, eine Vision
            </h2>
            <p className="mx-auto max-w-2xl text-muted-foreground">
              Entwicklungsexpertise trifft Vertriebsstärke – seit 2025 arbeiten wir gemeinsam daran, 
              Ihnen die beste Rechnungssoftware zu liefern.
            </p>
          </div>
          
          <div className="mx-auto max-w-5xl">
            <div className="grid items-stretch gap-8 lg:grid-cols-2">
              {/* Lizenzwerk */}
              <div className="group">
                <div className="h-full overflow-hidden rounded-3xl border border-border bg-card p-8 transition-all duration-300 hover:border-primary/30 hover:shadow-xl lg:p-10">
                  <div className="mb-6 flex items-center gap-4">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10">
                      <ShoppingBag className="h-7 w-7 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-display text-xl font-bold text-foreground">Lizenzwerk</h3>
                      <p className="text-sm text-muted-foreground">Vertrieb & Distribution</p>
                    </div>
                  </div>
                  
                  <p className="mb-6 text-muted-foreground leading-relaxed">
                    Seit 2024 haben wir uns auf den Vertrieb von Software und Lizenzen spezialisiert. 
                    Mit unserem eigenen Online-Shop sowie Präsenzen auf Amazon und eBay erreichen wir 
                    Kunden dort, wo sie einkaufen möchten.
                  </p>
                  
                  <div className="flex flex-wrap gap-2">
                    <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                      Eigener Shop
                    </span>
                    <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                      Amazon
                    </span>
                    <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                      eBay
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Launix */}
              <div className="group">
                <div className="h-full overflow-hidden rounded-3xl border border-border bg-card p-8 transition-all duration-300 hover:border-accent/30 hover:shadow-xl lg:p-10">
                  <div className="mb-6 flex items-center gap-4">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-accent/10">
                      <Code className="h-7 w-7 text-accent" />
                    </div>
                    <div>
                      <h3 className="font-display text-xl font-bold text-foreground">Launix</h3>
                      <p className="text-sm text-muted-foreground">Entwicklung & Innovation</p>
                    </div>
                  </div>
                  
                  <p className="mb-6 text-muted-foreground leading-relaxed">
                    Die Entwickler hinter NextInvoice und weiteren professionellen Enterprise-Lösungen. 
                    Launix steht für maßgeschneiderte Software, die sich an Ihre Bedürfnisse anpasst.
                  </p>
                  
                  <div className="flex flex-wrap gap-2">
                    <span className="rounded-full bg-accent/10 px-3 py-1 text-xs font-medium text-accent">
                      Enterprise Software
                    </span>
                    <span className="rounded-full bg-accent/10 px-3 py-1 text-xs font-medium text-accent">
                      Custom Development
                    </span>
                    <span className="rounded-full bg-accent/10 px-3 py-1 text-xs font-medium text-accent">
                      Made in Germany
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sales Channels */}
      <section className="bg-background py-20 lg:py-28">
        <div className="container">
          <div className="mb-16 text-center">
            <h2 className="mb-4 font-display text-3xl font-bold text-foreground sm:text-4xl">
              Kaufen Sie dort, wo Sie möchten
            </h2>
            <p className="mx-auto max-w-2xl text-muted-foreground">
              Ob direkter Kontakt oder bekannter Marktplatz – wir sind da, wo Sie uns brauchen.
            </p>
          </div>

          <div className="mx-auto grid max-w-4xl gap-6 sm:grid-cols-3">
            {channels.map((channel) => (
              <div
                key={channel.title}
                className="group relative overflow-hidden rounded-2xl border border-border bg-card p-6 text-center transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-lg"
              >
                <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full bg-primary/5 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                
                <div className="relative">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 transition-transform duration-300 group-hover:scale-110">
                    <channel.icon className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="mb-2 font-display text-lg font-bold text-foreground">
                    {channel.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {channel.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="relative overflow-hidden bg-secondary/30 py-20 lg:py-28">
        <div className="absolute inset-0 pattern-grid opacity-50" />
        
        <div className="container relative">
          <div className="mb-16 text-center">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-4 py-2">
              <Rocket className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold text-primary">Unsere Geschichte</span>
            </div>
            <h2 className="font-display text-3xl font-bold text-foreground sm:text-4xl">
              Von der Idee zur Partnerschaft
            </h2>
          </div>

          <div className="mx-auto max-w-4xl">
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-8 top-0 h-full w-1 rounded-full bg-gradient-to-b from-primary via-primary/50 to-accent lg:left-1/2 lg:-translate-x-1/2" />
              
              {timeline.map((item, index) => (
                <div
                  key={index}
                  className={`relative mb-12 flex items-start gap-8 lg:mb-16 ${
                    index % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"
                  }`}
                >
                  {/* Dot */}
                  <div className={`absolute left-8 z-10 flex h-8 w-8 -translate-x-1/2 items-center justify-center rounded-full border-4 border-background lg:left-1/2 ${
                    item.highlight ? "bg-accent" : "bg-primary"
                  }`}>
                    <item.icon className="h-4 w-4 text-white" />
                  </div>
                  
                  {/* Content */}
                  <div className={`ml-16 flex-1 lg:ml-0 ${index % 2 === 0 ? "lg:pr-20 lg:text-right" : "lg:pl-20"}`}>
                    <div className={`rounded-2xl border bg-card p-6 transition-all duration-300 hover:shadow-lg ${
                      item.highlight ? "border-accent/30 ring-1 ring-accent/20" : "border-border hover:border-primary/30"
                    }`}>
                      <span className={`font-display text-3xl font-bold ${item.highlight ? "text-accent" : "text-primary"}`}>
                        {item.year}
                      </span>
                      <h3 className="mb-2 font-display text-xl font-bold text-foreground">
                        {item.title}
                      </h3>
                      <p className="text-muted-foreground">
                        {item.description}
                      </p>
                      {item.highlight && (
                        <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-accent/10 px-3 py-1 text-xs font-semibold text-accent">
                          <Handshake className="h-3 w-3" />
                          Meilenstein
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Spacer for alternating layout */}
                  <div className="hidden flex-1 lg:block" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-background py-20 lg:py-28">
        <div className="container">
          <div className="mb-16 text-center">
            <h2 className="mb-4 font-display text-3xl font-bold text-foreground sm:text-4xl">
              Wofür wir stehen
            </h2>
            <p className="mx-auto max-w-2xl text-muted-foreground">
              Diese Prinzipien leiten uns bei allem, was wir tun.
            </p>
          </div>

          <div className="mx-auto grid max-w-5xl gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((value) => (
              <div
                key={value.title}
                className="group rounded-2xl border border-border bg-card p-6 text-center transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-lg"
              >
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 transition-transform duration-300 group-hover:scale-110">
                  <value.icon className="h-7 w-7 text-primary" />
                </div>
                <h3 className="mb-2 font-display text-lg font-bold text-foreground">
                  {value.title}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location CTA */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary via-primary to-primary/90 py-20 lg:py-28">
        <div className="absolute inset-0 pattern-grid opacity-10" />
        <div className="absolute -left-32 -top-32 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-accent/20 blur-3xl" />
        
        <div className="container relative">
          <div className="mx-auto max-w-3xl text-center">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 backdrop-blur-sm">
              <MapPin className="h-4 w-4 text-white" />
              <span className="text-sm font-semibold text-white">Neugersdorf, Sachsen</span>
            </div>
            
            <h2 className="mb-6 font-display text-3xl font-bold text-white sm:text-4xl lg:text-5xl">
              100% Made in Germany
            </h2>
            
            <p className="mb-4 text-lg text-white/80">
              Entwicklung, Vertrieb und Support – alles aus einer Hand, alles aus Sachsen.
            </p>
            
            <div className="mb-10 flex flex-wrap items-center justify-center gap-4">
              <div className="flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 backdrop-blur-sm">
                <Building2 className="h-4 w-4 text-accent" />
                <span className="text-sm text-white">Lizenzwerk</span>
              </div>
              <div className="text-white/50">+</div>
              <div className="flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 backdrop-blur-sm">
                <Code className="h-4 w-4 text-accent" />
                <span className="text-sm text-white">Launix</span>
              </div>
              <div className="text-white/50">=</div>
              <div className="flex items-center gap-2 rounded-full bg-accent/20 px-4 py-2 backdrop-blur-sm">
                <Handshake className="h-4 w-4 text-accent" />
                <span className="text-sm font-semibold text-white">NextInvoice</span>
              </div>
            </div>
            
            <Button
              size="lg"
              className="h-14 bg-accent px-10 text-base font-semibold text-accent-foreground shadow-xl shadow-accent/30 transition-all duration-300 hover:scale-105 hover:bg-accent/90"
              asChild
            >
              <Link to="/preise">
                Jetzt kostenlos testen
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            
            <p className="mt-4 text-sm text-white/60">
              1 Monat kostenlos · Keine Kreditkarte erforderlich
            </p>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default About;